import { Signal } from '@angular/core';
import { Hotspot, IDisplaySlide, SlideType } from '@pcp/core/interfaces';
import { ITransferDynamicContentSlide } from '@pcp/streaming';
import { FixedImageBackground, resolveBackground, SlideBackground } from './slide-background';
import { SlideCapability, SlideHostContext } from './slide-capability';
import { HotspotLayer } from './capabilities/hotspot-layer';
import { BookpagePaging, PagingSide } from './capabilities/bookpage-paging';
import { GraphicOverlay } from './capabilities/graphic-overlay';
import { ScrollVideoScrub } from './capabilities/scroll-video-scrub';
import { TextboardContent } from './capabilities/textboard-content';
import { resolveTextboardStyles } from './capabilities/textboard-styles';
import { resolvePagingIconUrls, SlideCanvasTextboardIconConfig } from './textboard-icon-config';

/**
 * The registry (`canvas-renderer-composition`, Decisions C3/C8/§4). A slide resolves to ONE
 * background strategy plus a list of capability specs — replacing the `slide.type` switch +
 * scattered presence-checks the monolithic component carried. Adding a slide type's canvas behavior
 * is a one-line entry in {@link INTRINSIC} + (if needed) a capability module — not a new conditional
 * in the host (Decision C8 / §4.3).
 */

/** Hotspot projection + intents (Decision C14/D6). */
export interface HotspotCallbacks {
    /** Intent UP: a hotspot region was activated (mouse:down inside it). */
    onActivate: (hotspot: Hotspot) => void;
    /** Synced set of deactivated hotspot indices, passed DOWN for projection. */
    deactivated: Signal<ReadonlySet<number>>;
    /** Intent UP: the writer activated a non-`forward` hotspot at this index — deactivate it in Y.Doc. */
    onDeactivate: (index: number) => void;
}

/** Bookpage paging availability + intent. */
export interface PagingCallbacks {
    /** Intent UP: the writer activated the previous / next corner affordance. */
    onPage: (side: PagingSide) => void;
    hasPreviousPage: Signal<boolean>;
    hasNextPage: Signal<boolean>;
}

/** Textboard page projection + intent, plus the brand config (design D5). */
export interface TextboardCallbacks {
    /** Synced per-layer textboard page (1-based), passed DOWN for projection (never re-emitted). */
    page: Signal<number | undefined>;
    /** Intent UP: show this textboard page (button click / writer append-jump). */
    onPageIntent: (page: number) => void;
    /**
     * Brand-specific paging-icon suffixes + text-area offsets. Injected by the renderer (the DI
     * token must be injected by a component); the factory below expands it into concrete deps.
     */
    config: SlideCanvasTextboardIconConfig;
}

/** Scroll-video scrub intent (`canvas-scroll-video-slides`). */
export interface ScrollVideoCallbacks {
    /**
     * Intent UP: the writer wheel-scrubbed one frame step. The host owns the index math, the
     * ephemeral live sync, and the settled persist — the capability only names the direction.
     */
    onScrub: (direction: 'left' | 'right') => void;
}

/**
 * Activation callbacks + reactive-input signals the host supplies to capability factories (C13),
 * grouped per capability so each factory's actual dependencies are visible at a glance.
 */
export interface CapabilityCallbacks {
    hotspots: HotspotCallbacks;
    paging: PagingCallbacks;
    /** Synced per-layer dynamic-content list, passed DOWN (graphic overlays + textboard). */
    dynamicContent: Signal<ITransferDynamicContentSlide[]>;
    textboard: TextboardCallbacks;
    scrollVideo: ScrollVideoCallbacks;
}

/**
 * A capability the recipe wants. `kind` identifies it for the host's reconcile (reuse-or-recreate
 * across slide changes); `create` instantiates it against the shared host context.
 */
export interface CapabilitySpec {
    readonly kind: string;
    create(ctx: SlideHostContext, cb: CapabilityCallbacks): SlideCapability;
}

export interface SlideRecipe {
    background: SlideBackground;
    /** Capability specs in PRIORITY order (first = highest pointer-claim priority, Decision C12). */
    capabilities: CapabilitySpec[];
}

const PAGING: CapabilitySpec = {
    kind: 'bookpage-paging',
    create: (ctx, cb) => new BookpagePaging(ctx, cb.paging.onPage, cb.paging.hasPreviousPage, cb.paging.hasNextPage),
};

const HOTSPOTS: CapabilitySpec = {
    kind: 'hotspots',
    create: (ctx, cb) =>
        new HotspotLayer(ctx, cb.hotspots.onActivate, cb.hotspots.deactivated, cb.hotspots.onDeactivate),
};

const GRAPHIC_OVERLAY: CapabilitySpec = {
    kind: 'graphic-overlay',
    create: (ctx, cb) => new GraphicOverlay(ctx, cb.dynamicContent),
};

const SCROLL_VIDEO_SCRUB: CapabilitySpec = {
    kind: 'scroll-video-scrub',
    create: (ctx, cb) => new ScrollVideoScrub(ctx, cb.scrollVideo.onScrub),
};

const TEXTBOARD_CONTENT: CapabilitySpec = {
    kind: 'textboard-content',
    // The factory is the wiring point for the textboard's concrete deps: the theme styles are
    // bridged from the canvas element's computed CSS (design D5 — re-resolved per creation), the
    // brand config expands into icon URLs + text-area offsets here, not in the renderer.
    create: (ctx, cb) =>
        new TextboardContent(ctx, {
            content: cb.dynamicContent,
            currentPage: cb.textboard.page,
            onPageIntent: cb.textboard.onPageIntent,
            styles: resolveTextboardStyles(ctx.canvas.getElement()),
            icons: resolvePagingIconUrls(cb.textboard.config),
            layout: {
                offsetRight: cb.textboard.config.layout.textOffsetRight,
                offsetBottom: cb.textboard.config.layout.textOffsetBottom,
            },
        }),
};

/**
 * Intrinsic capabilities by slide type (always present for that type). Data-gated capabilities
 * (hotspots) are added by refinement below — they are type-independent. **Adding a type's canvas
 * behavior is ONE entry here**, not a host edit. `textboard` / `textboard_generated` are
 * render-identical (`canvas-textboard-slides`), so both map to the same capability.
 */
const INTRINSIC: Partial<Record<SlideType, CapabilitySpec[]>> = {
    [SlideType.bookpage]: [PAGING],
    [SlideType.graphicboard]: [GRAPHIC_OVERLAY],
    [SlideType.textboard]: [TEXTBOARD_CONTENT],
    [SlideType.textboard_generated]: [TEXTBOARD_CONTENT],
    [SlideType.scrollVideo]: [SCROLL_VIDEO_SCRUB],
};

/** Types that refuse the data-gated hotspot capability (spec `canvas-textboard-slides`): the
 * textboard fills the canvas with dynamic text, so authored hotspot regions would be unreachable
 * noise — deny + log instead of silently hit-testing under the text. */
const HOTSPOT_DENYING_TYPES: ReadonlySet<SlideType> = new Set([SlideType.textboard, SlideType.textboard_generated]);

function hasValidPanorama(slide: IDisplaySlide): boolean {
    const p = slide.panorama;
    return !!p && p.width > 0 && p.height > 0;
}

/**
 * Resolve the background strategy + capabilities for a slide. Strict, observable, fail-safe
 * (Decision C3 / §4.2): malformed background data degrades to a fixed background and logs via the
 * supplied `warn`, rather than throwing or blanking the canvas; non-permitted extra data (hotspots
 * on a textboard) is dropped with a warning.
 */
export function resolveRecipe(slide: IDisplaySlide, warn?: (message: string) => void): SlideRecipe {
    const capabilities: CapabilitySpec[] = [...(INTRINSIC[slide.type] ?? [])];

    // Data-presence refinement: hotspots are type-independent (any image slide may carry them) —
    // except on the deny-list types above.
    const hotspotCount = slide.hotspots?.length ?? 0;
    if (hotspotCount > 0) {
        if (HOTSPOT_DENYING_TYPES.has(slide.type)) {
            warn?.(`Slide type '${slide.type}' does not permit hotspots; ignoring ${hotspotCount} hotspot(s).`);
        } else {
            capabilities.push(HOTSPOTS);
        }
    }

    let background = resolveBackground(slide);
    // Degrade + log: a non-bookpage slide whose panorama is malformed falls back to a fixed
    // background (a PanoramaBackground would divide by a zero/negative dimension).
    if (slide.type !== SlideType.bookpage && slide.panorama && !hasValidPanorama(slide)) {
        warn?.(
            `Slide type '${slide.type}' has a malformed panorama (${slide.panorama.width}×${slide.panorama.height}); falling back to a fixed background.`,
        );
        background = new FixedImageBackground();
    }

    return { background, capabilities };
}
